/**
 * Панель этажерки здания («Объём» из контура, фаза P): чипы этажей с числом
 * арендаторов, список арендаторов активного этажа (тап — их карточка),
 * выход. Слэбы на карте — map/floor-stack.ts; тап по слэбу тоже меняет этаж.
 */
import { tr } from '@app/i18n'
import type { Venue } from '@app/rvf'
import { useEffect } from 'preact/hooks'
import { selectTarget } from '../actions'
import { markerVisual } from '../map/marker-icons'
import { promoTiers } from '../promo'
import { floorStackFloor, floorStackUnitId, langTick, venue, venueMap } from '../state'
import { featureName } from './labels'
import { buildingFloorsOf, tenantsOnFloor } from './target-card-full'

export function closeFloorStack(): void {
  venueMap.value?.hideFloorStack()
  floorStackUnitId.value = null
  floorStackFloor.value = 1
}

function pickFloor(n: number): void {
  floorStackFloor.value = n
  venueMap.value?.setFloorStackFloor(n)
}

function FloorChips({ v, unitId, floors }: { v: Venue; unitId: string; floors: number }) {
  return (
    <div class="fs-chips">
      {Array.from({ length: floors }, (_, i) => i + 1).map((n) => {
        const count = tenantsOnFloor(v, unitId, n).length
        return (
          <button
            key={n}
            type="button"
            class={`fs-chip${floorStackFloor.value === n ? ' fs-chip-on' : ''}`}
            onClick={() => pickFloor(n)}
          >
            <b>{n}F</b>
            <span>{count > 0 ? count : '—'}</span>
          </button>
        )
      })}
    </div>
  )
}

function FloorTenants({ v, unitId }: { v: Venue; unitId: string }) {
  const list = tenantsOnFloor(v, unitId, floorStackFloor.value)
  if (list.length === 0) {
    return <p class="fs-empty">{tr('floorstack.vacant', { n: String(floorStackFloor.value) })}</p>
  }
  return (
    <div class="fs-tenants">
      {list.map((p) => {
        const { svg, color } = markerVisual(p.category)
        return (
          <button
            key={p.id}
            type="button"
            class={`card-tenant-row${promoTiers.value.has(p.id) ? ' promo-row' : ''}`}
            onClick={() => {
              closeFloorStack()
              selectTarget(p.id)
            }}
          >
            <span
              class="card-tenant-icon"
              style={{ '--mk': color }}
              dangerouslySetInnerHTML={{ __html: svg }}
            />
            <span class="card-tenant-name">{featureName(v, p.id)}</span>
            {promoTiers.value.has(p.id) && <i class="promo-chip">{tr('promo.chip')}</i>}
          </button>
        )
      })}
    </div>
  )
}

export function FloorStackPanel() {
  langTick.value
  const v = venue.value
  const unitId = floorStackUnitId.value

  // Слэбы на карте живут, пока панель открыта.
  useEffect(() => {
    if (!v || !unitId) return
    const floors = buildingFloorsOf(v, unitId)
    const ok = venueMap.value?.showFloorStack(unitId, floors, pickFloor)
    if (!ok) {
      floorStackUnitId.value = null
      return
    }
    venueMap.value?.setFloorStackFloor(floorStackFloor.value)
    return () => venueMap.value?.hideFloorStack()
  }, [v, unitId])

  if (!v || !unitId) return null
  const floors = buildingFloorsOf(v, unitId)

  return (
    <section class="fs-panel glass" aria-label={tr('floorstack.title')}>
      <div class="fs-head">
        <div>
          <p class="tech-label">{tr('floorstack.title')}</p>
          <h2 class="fs-title">{featureName(v, unitId)}</h2>
        </div>
        <button
          type="button"
          class="icon-btn"
          aria-label={tr('common.close')}
          onClick={closeFloorStack}
        >
          ✕
        </button>
      </div>
      <FloorChips v={v} unitId={unitId} floors={floors} />
      <FloorTenants v={v} unitId={unitId} />
      <p class="fs-hint">{tr('floorstack.hint')}</p>
    </section>
  )
}
